/* import __COLOCATED_TEMPLATE__ from './react-shim.hbs'; */
var _class, _descriptor;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { later, schedule } from '@ember/runloop';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import YieldWrapper from './yield-wrapper';
import { mountRootParcel } from 'single-spa';
import { DEFAULT_CONFIG, injectComponentHotReloadScripts, removeReactDevScripts } from '../utils/react-library';
import { hash } from 'rsvp';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';
import { datadogRum } from '@datadog/browser-rum';
/**
 * This is a workaround to test that await render() in Ember tests can assert
 * the error message thrown by the component.
 * Need to wrap around schedule() since the renderComponent is an async method.
 * This is NOT needed if renderComponent is not async.
 */
function throwEmberTestableError(message) {
  schedule('afterRender', () => {
    throw new Error(message);
  });
}

/**
 * This is a generic React component shim that can be used to render any React component in Ember
 * You'll need to extend this class and implement the `library` method to return the React component library
 *
 * import ReactShim from '@square/react-shim/components/react-shim';
 * import MyReactLibrary from '@square/my-react-library';
 *
 * export default class ExampleReactLibraryContainer extends ReactShim {
 *  library = () => MyReactLibrary;
 * }
 *
 * Then you can create React components in your Ember templates like so:
 * <ExampleReactLibraryContainer
 *    @componentName="MyReactComponent"
 *    @prop1="value1"
 *    @prop2="value2"
 *    @onChange={{this.onChange}}
 * />
 *
 * ReactShim
 * 1. HMR mode
 * - Uses vite hmr + single-spa to mount the component and enable hot-reloading
 * - Works only if you're serving your components from a local server to http://localhost:4201/src/dashboard-modules
 * - check `injectComponentHotReloadScripts` for more details
 * - Only available for local development
 * 2. Library mode
 * - Loads your components the "normal" way by importing from node_modules.
 * - No hot module reloading available
 */
let ReactShim = (_class = class ReactShim extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "parcel", void 0);
    _defineProperty(this, "root", void 0);
    _initializerDefineProperty(this, "isInHmrMode", _descriptor, this);
  }
  get elementId() {
    return `react-component-${guidFor(this)}`;
  }
  library() {
    throwEmberTestableError('Must extend ReactComponent and implement "library" method.');
    return {};
  }
  componentWrapper() {
    // If you want to use a custom component wrapper, you can override this method in your class extension
    // If not implemented, we will not use any component wrapper
    return 'div';
  }
  componentWrapperProps() {
    // if using custom component wrapper, you can override this method in your class extension and pass props here
    return {};
  }
  devConfig() {
    // if you need to pass any config for your React library, you can override this method in your class extension
    return DEFAULT_CONFIG;
  }
  loadLazyModules() {
    return hash({
      React: import('react'),
      ReactDOM: import('react-dom/client'),
      ReactComponentLibrary: this.library(),
      ComponentWrapper: this.componentWrapper()
    });
  }

  /**
   * This is a workaround to avoid passing args individually to {{did-update}} ember modifier.
   * Consider: <ReactShim @componentName="foo" @title={{this.title}} @subtitle={{this.subTitle}} ... />
   *
   * To update the React component when @title or @subtitle are updated, we'd need to use the `did-update` modifier
   * e.g. {{did-update this.willUpdateComponent @title @subtitle }}
   *
   * But since this is a common React container, we'd need to define ALL possible args for ALL possible react components
   * e.g. to support <ReactShim @componentName="bar" @arg1={{..}} @arg2={{..}} @arg3={{..}} />
   * we'd need to use - {{did-update this.willUpdateComponent @title @subtitle @arg1 @arg2 @arg3 }}
   *
   * Ideally, we could've just done this - {{did-update this.willUpdateComponent this.args}}
   * But this doesn't call this.willUpdateComponent because for auto-tracking to work the individual tracked variable needs to be accessed
   * So calling "Object.values(this.args)" here will ensure that all args are accessed and tracked.
   *
   * This is what did-update does under the hood for all arguments passed to it.
   * https://github.com/emberjs/ember-render-modifiers/blob/v2.1.0/addon/modifiers/did-update.js#L88-L92
   * https://github.com/emberjs/ember.js/issues/19277#issuecomment-728979442
   * https://github.com/ember-modifier/ember-modifier/pull/63#issuecomment-815908201
   *
   * Great article for more reading - v5.chriskrycho.com/journal/autotracking-elegant-dx-via-cutting-edge-cs/#3-recomputing
   */
  get glimmerReactiveProps() {
    this.args && Object.values(this.args);
    return this.args;
  }

  // Necessary due to the order of operations in which renderComponent may be called - passed props may
  // change between two different asynchronous calls, and the props we are using at the time of render must reflect
  // the most up to date.
  get commonProps() {
    return {
      ...this.args
    };
  }
  async reactChildrenFromEmberChildren(domElement) {
    const emberChildren = domElement.hasChildNodes() ? domElement.childNodes : null;
    if (!emberChildren) {
      return [];
    }
    const {
      React
    } = await this.loadLazyModules();
    return React.createElement(YieldWrapper, {
      key: this.elementId,
      nodes: [...domElement.childNodes]
    });
  }

  // waitFor is needed to make sure dynamic imports are resolved before rendering the component
  // https://github.com/embroider-build/ember-auto-import/issues/264#issuecomment-625706378
  async renderComponent(domElement) {
    if (!this.args.componentName) {
      throwEmberTestableError('componentName prop is required');
      return;
    }
    const {
      componentName
    } = this.args;
    const children = await this.reactChildrenFromEmberChildren(domElement);
    try {
      // For development and staging, we use single-spa to mount the component and enable hot-reloading
      const lifecycle = await injectComponentHotReloadScripts(this.devConfig());
      const parcelProps = {
        ...this.commonProps,
        domElement,
        children
      };
      this.parcel = mountRootParcel(lifecycle, parcelProps);
      await this.parcel?.mountPromise;
      const {
        reactType
      } = this.args;
      datadogRum.setViewContextProperty('reactType', reactType || 'unknown');
      this.isInHmrMode = true;
    } catch {
      // When not in development or staging, we use the build-time rendering
      const {
        ReactComponentLibrary,
        ReactDOM
      } = await this.loadLazyModules();
      if (!(componentName in ReactComponentLibrary)) {
        throwEmberTestableError(`React library: ${componentName} doesn't exist on your React library`);
        return;
      }
      this.root = ReactDOM.createRoot(domElement);
      await this.updateComponent(this.commonProps, children);

      // `react-shim` initializes with `display: none` to prevent children from being rendered before React is ready
      // Once React is ready, we remove the `display: none` to render the component
      // we need to use setTimeout to ensure that the display property is removed after React is finished with its render cycle
      later(() => {
        domElement.style.removeProperty('display');
      }, 0);
    }
  }
  async updateComponent(props, children) {
    const {
      React,
      ReactComponentLibrary,
      ComponentWrapper
    } = await this.loadLazyModules();
    const {
      componentName
    } = props;
    const Component = React.createElement(ReactComponentLibrary[componentName], props, children);
    const Wrapped = React.createElement(ComponentWrapper, this.componentWrapperProps(), Component);
    this.root?.render(Wrapped);
  }
  async willUpdateComponent(domElement) {
    const commonProps = {
      ...this.commonProps
    };
    const children = await this.reactChildrenFromEmberChildren(domElement);
    if (this.parcel) {
      // running in HMR mode
      this.parcel?.update?.({
        ...commonProps,
        children,
        domElement
      });
    } else {
      // running in library mode
      this.updateComponent(commonProps, children);
    }
  }
  destroyComponent() {
    this.root?.unmount();
    // This ensures that calling root unmount/render will not throw an error after the root has been unmounted.
    // Calling this.root.unmount() does not actually reset the root to an undefined state.
    this.root = null;
    this.parcel?.unmount();
    removeReactDevScripts(this.devConfig());
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "isInHmrMode", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "renderComponent", [action, waitFor], Object.getOwnPropertyDescriptor(_class.prototype, "renderComponent"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "willUpdateComponent", [action], Object.getOwnPropertyDescriptor(_class.prototype, "willUpdateComponent"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "destroyComponent", [action], Object.getOwnPropertyDescriptor(_class.prototype, "destroyComponent"), _class.prototype), _class);
export { ReactShim as default };